import { useEffect, useMemo, useRef, useState } from "react";
import { fetchData } from "../api/client";
import { SALES_PIPELINE_UNSET_SUBTIPO1_STAGE_ID } from "../data/salesPipeline/constants";

const SALES_PIPELINE_URL = "/sales-pipeline/leads";

const fieldForLevel = (level) =>
  level <= 0 ? "id_tipo" : level === 1 ? "id_subtipo1" : level === 2 ? "id_subtipo2" : "id_subtipo3";

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    const dt = new Date(iso);
    const diff = Date.now() - dt.getTime();
    const m = Math.round(diff / 60000);
    if (m <= 0) return "agora mesmo";
    if (m < 60) return `há ${m} min`;
    const h = Math.round(m / 60);
    if (h < 24) return `há ${h} h`;
    return dt.toLocaleString("pt-BR");
  } catch {
    return "";
  }
};

/**
 * 🔹 Mapeia 100% do contrato da API
 * 🔹 Mantém compatibilidade com cards antigos
 */
/** Exportado para deep link `/sales-pipeline/:id` quando o card ainda não está nas colunas carregadas. */
export const mapSalesPipelineLeadRow = (row = {}) => {
  const items = Array.isArray(row.items) ? row.items : [];

  const subtotalFromItems = items.reduce(
    (s, it) => s + (Number(it.price) || 0) * (Number(it.qty) || 0),
    0
  );

  const subtotal = Number(row.subtotal ?? subtotalFromItems) || 0;
  const shippingCost = Number(row.shippingCost ?? 0) || 0;
  const total = Number(row.total ?? subtotal + shippingCost) || subtotal + shippingCost;

  return {
    /* ===== Identidade ===== */
    id: row.id,
    status: row.status,
    statusLabel: row.statusLabel,
    orderCode: row.orderCode,
    tags: row.tags ?? null,
    companyName: row.companyName,
    channelType: row.channelType,
    nf: row.nf ?? null,
    nsu: row.nsu ?? null,

    /* ===== Datas ===== */
    createdAt: row.createdAt ?? null,
    createdAtLabel: fmtWhen(row.createdAtLabel || row.createdAt),

    /* ===== Valores ===== */
    subtotal,
    shippingCost,
    total,

    /* ===== Itens ===== */
    items,

    /* ===== Cliente (NOVO – ESSENCIAL) ===== */
    client: row.client
      ? {
          id: row.client.id ?? null,
          type: row.client.type ?? null, // PF | PJ
          name: row.client.name ?? "",
          document: row.client.document ?? null, // CPF/CNPJ
          email: row.client.email ?? null,
          phone: row.client.phone ?? null,
          tags: row.client.tags ?? null,
        }
      : null,

    /* ===== Pagamento ===== */
    payment: row.payment
      ? {
          status: row.payment.status ?? null,
          statusLabel: row.payment.statusLabel ?? row.payment.status ?? null,
          method: row.payment.method ?? null,
          methodTipo: row.payment.methodTipo ?? null,
          methodLabel: row.payment.methodLabel ?? null,
          transactionId: row.payment.transactionId ?? null,
          cardBrand: row.payment.bandeira_cartao ?? null,
          installments: row.payment.parcelas ?? null,
        }
      : null,

    /* ===== Antifraude ===== */
    antifraud: row.antifraud
      ? {
          status: row.antifraud.status ?? null,
          statusLabel: row.antifraud.statusLabel ?? null,
          score: typeof row.antifraud.score === "number" ? row.antifraud.score : null,
        }
      : null,

    /* ===== Envio ===== */
    shipping: row.shipping
      ? {
          status: row.shipping.status ?? null,
          statusLabel: row.shipping.statusLabel ?? null,
          method: row.shipping.method ?? null,
          methodLabel: row.shipping.methodLabel ?? null,
          carrier: row.shipping.carrier ?? null,
          tracking: row.shipping.tracking ?? null,
          eta: row.shipping.eta ?? null,
        }
      : null,

    /* ===== Endereço ===== */
    address: row.address
      ? {
          street: row.address.street ?? null,
          number: row.address.number ?? null,
          district: row.address.district ?? null,
          city: row.address.city ?? null,
          uf: row.address.uf ?? null,
          zip: row.address.zip ?? null,
        }
      : null,

    /* ===== Usuário responsável ===== */
    user: row.user
      ? {
          id: row.user.id ?? null,
          name: row.user.name ?? "",
          avatar: row.user.avatar ?? null,
        }
      : null,

    cancellation: row.cancellation
      ? {
          enabled: Boolean(row.cancellation.enabled),
          canRequest: Boolean(row.cancellation.canRequest),
          alreadyCancelled: Boolean(row.cancellation.alreadyCancelled),
          unlimited: Boolean(row.cancellation.unlimited),
          windowSecondsLeft: row.cancellation.windowSecondsLeft ?? null,
          reason: row.cancellation.reason ?? null,
        }
      : null,
  };
};

export const useSalesPipelineCards = ({
  visibleStages = [],
  level = 0,
  limit = 50,
  /** No nível subtipo1, restringe a coluna "sem etapa" ao tipo comercial pai (breadcrumb). */
  pipelineTipoId = null,
}) => {
  const [cardsByStage, setCardsByStage] = useState({});
  const [pageByStage, setPageByStage] = useState({});
  const [hasMoreByStage, setHasMoreByStage] = useState({});
  const [loadingByStage, setLoadingByStage] = useState({});
  const [error, setError] = useState(null);
  const [totalsByStage, setTotalsByStage] = useState({});
  const [searchDisplayByStage, setSearchDisplayByStage] = useState({});
  const stageSearchByIdRef = useRef({});

  const field = fieldForLevel(level);
  const stageIds = useMemo(() => (visibleStages || []).map((s) => s.id), [visibleStages]);

  useEffect(() => {
    const init = {};
    stageIds.forEach((id) => (init[id] = []));
    setCardsByStage(init);
    setPageByStage({});
    setHasMoreByStage({});
    setLoadingByStage({});
    setTotalsByStage({});
    setSearchDisplayByStage({});
    stageSearchByIdRef.current = {};
    setError(null);

    stageIds.forEach((id) => loadPage(id, 1, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field, level, JSON.stringify(stageIds), pipelineTipoId]);

  const safeGetArrayFromResponse = (resp) => {
    if (!resp) return [];
    if (Array.isArray(resp)) return resp;
    if (Array.isArray(resp.data)) return resp.data;
    if (resp.data && Array.isArray(resp.data.data)) return resp.data.data;
    return [];
  };

  async function loadPage(stageId, page = 1, replace = false) {
    if (!stageId) return;
    setLoadingByStage((m) => ({ ...m, [stageId]: true }));

    try {
      const qs = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        /* Pode passar "0" quando o modal passar a carregar itens sob demanda (menos carga no BD). */
        include_products: "1",
      });
      if (level === 1 && stageId === SALES_PIPELINE_UNSET_SUBTIPO1_STAGE_ID) {
        qs.set("id_subtipo1_unset", "1");
        const tid = Number(pipelineTipoId);
        if (Number.isFinite(tid) && tid > 0) {
          qs.set("id_tipo", String(tid));
        }
      } else {
        qs.set(field, String(stageId));
      }

      const term = String(stageSearchByIdRef.current[stageId] || "").trim();
      if (term) qs.set("search", term);

      const url = `${SALES_PIPELINE_URL}?${qs.toString()}`;
      const resp = await fetchData(url, "GET");

      const rows = safeGetArrayFromResponse(resp);
      const cards = rows.map(mapSalesPipelineLeadRow);

      setCardsByStage((m) => ({
        ...m,
        [stageId]: replace ? cards : [...(m[stageId] || []), ...cards],
      }));

      setPageByStage((m) => ({ ...m, [stageId]: page }));

      const totalFromApi =
        resp?.pagination?.total != null ? Number(resp.pagination.total) : null;
      if (totalFromApi != null && Number.isFinite(totalFromApi)) {
        setTotalsByStage((m) => ({ ...m, [stageId]: totalFromApi }));
      }

      const hasMore =
        resp?.pagination?.total != null
          ? page * limit < Number(resp.pagination.total)
          : cards.length === limit;

      setHasMoreByStage((m) => ({ ...m, [stageId]: hasMore }));
    } catch (e) {
      console.error("Falha ao carregar cards do estágio", stageId, e);
      setError(e);
      setHasMoreByStage((m) => ({ ...m, [stageId]: false }));
    } finally {
      setLoadingByStage((m) => ({ ...m, [stageId]: false }));
    }
  }

  const loadMore = (stageId) => {
    const p = pageByStage[stageId] || 1;
    if (!hasMoreByStage[stageId] || loadingByStage[stageId]) return;
    return loadPage(stageId, p + 1, false);
  };

  const updateLeadStageAsync = async (leadId, level, targetStageId) => {
    const payload =
      targetStageId === SALES_PIPELINE_UNSET_SUBTIPO1_STAGE_ID
        ? { level, targetStageId: 0 }
        : { level, targetStageId };
    return fetchData(`${SALES_PIPELINE_URL}/${leadId}/stage`, "POST", payload);
  };

  const refreshAll = async () => {
    await Promise.all(stageIds.map((id) => loadPage(id, 1, true)));
  };

  async function applyStageSearch(stageId, rawTerm) {
    const t = String(rawTerm || "").trim();
    if (!t) {
      delete stageSearchByIdRef.current[stageId];
      setSearchDisplayByStage((m) => {
        const next = { ...m };
        delete next[stageId];
        return next;
      });
    } else {
      stageSearchByIdRef.current[stageId] = t;
      setSearchDisplayByStage((m) => ({ ...m, [stageId]: t }));
    }
    await loadPage(stageId, 1, true);
  }

  const loading = useMemo(
    () => Object.values(loadingByStage).some(Boolean),
    [loadingByStage]
  );

  const moveCardBetweenStages = (fromStageId, toStageId, cardId) => {
    if (!fromStageId || !toStageId || !cardId) return;

    setCardsByStage((prev) => {
      const from = Array.isArray(prev[fromStageId]) ? [...prev[fromStageId]] : [];
      const to = Array.isArray(prev[toStageId]) ? [...prev[toStageId]] : [];

      const idx = from.findIndex((c) => c.id === cardId);
      if (idx === -1) return prev;

      const [card] = from.splice(idx, 1);

      // Coloca no topo da coluna destino (UX correto para pipeline)
      to.unshift(card);

      return {
        ...prev,
        [fromStageId]: from,
        [toStageId]: to,
      };
    });
  };


  return {
    cardsByStage,
    loading,
    error,
    hasMoreByStage,
    loadingByStage,
    totalsByStage,
    stageSearchDisplayByStage: searchDisplayByStage,
    loadMore,
    moveCardBetweenStages,
    updateLeadStageAsync,
    refreshAll,
    applyStageSearch,
  };
};
